/**
 * depotUpgradeTree — Arbre de dépendances explicite des investissements
 * Dépôt / Énergie / Bureau & Tech (QG).
 *
 * 10/02/2026 (Lot K) — Demande user : « Je veux faire une nouvelle vue
 * pour le développement des dépôts à la place de la liste, un arbre de
 * développement selon les dépendances, le plus graphique possible. À
 * chaque investissement un renvoie vers la représentation en image du
 * dépôt avec l'apparition de l'investissement visuellement. »
 *
 * Structure :
 *   • node.id        ← investment id (data/investments.js)
 *   • node.parents   ← liste des `investmentId` prérequis (lien ET)
 *   • node.layer     ← 0=root, 1=base, 2=struct, 3=spécialisation, 4=opt.
 *   • node.column    ← position colonne dans la layer (0 = gauche)
 *   • node.zoneId    ← zone DEPOT_ZONES affectée (pour highlight)
 *   • node.icon      ← icône lucide
 *
 * Les coordonnées x/y sont calculées par layer×column pour un rendu
 * propre et déterministe. Les liens parent→enfant sont dessinés en
 * Bezier dans le composant DepotUpgradeTree.
 */
import {
  Wrench, Warehouse, Building, ArrowUpRight, HardHat, Box,
  Thermometer, Globe, AlertTriangle, BatteryCharging,
  Fuel, Droplet, Sun,
} from 'lucide-react';

/**
 * 13/07/2026 — Layout optimisé (demande user : « flèches qui ne traversent
 * pas les cases, phases débloquées/déblocables au même niveau »).
 *
 * Règles du layout :
 *   • layer = PROFONDEUR de dépendance réelle (0=garage, 1=entrepôt,
 *     2=tout ce qui ne demande que l'entrepôt, 3=tout ce qui demande le
 *     quai/la cuve, 4=premium). Résultat : chaque flèche relie deux rangées
 *     ADJACENTES → elle circule dans le couloir entre les rangées et ne
 *     traverse jamais une case. Les investissements déblocables en même
 *     temps sont alignés sur la même rangée.
 *   • parents = flèches AFFICHÉES (le prérequis le plus profond ; la
 *     chaîne implicite via ce parent couvre le reste). Le gating réel
 *     reste `conditionCheck` (data/investments.js).
 *   • une seule case par cellule (layer, column).
 */
export const UPGRADE_TREE_NODES = [
  // ─── NIVEAU 0 — Fondation ──────────────────────────────────────────
  { id: 'inv-depot-01', layer: 0, column: 2, parents: [],                zoneId: 'garage',   icon: Wrench,    color: 'amber' },

  // ─── NIVEAU 1 — Structure ──────────────────────────────────────────
  { id: 'inv-depot-02', layer: 1, column: 2, parents: ['inv-depot-01'],  zoneId: 'entrepot', icon: Warehouse, color: 'amber' },

  // ─── NIVEAU 2 — Déblocables dès l'entrepôt ─────────────────────────
  { id: 'inv-depot-04', layer: 2, column: 0, parents: ['inv-depot-02'],  zoneId: 'quai',     icon: ArrowUpRight, color: 'emerald' },
  { id: 'inv-depot-03', layer: 2, column: 1, parents: ['inv-depot-02'],  zoneId: 'entrepot', icon: Building,  color: 'amber' },
  { id: 'inv-depot-06', layer: 2, column: 2, parents: ['inv-depot-02'],  zoneId: 'garage',   icon: Globe,     color: 'sky' },
  { id: 'inv-depot-12', layer: 2, column: 3, parents: ['inv-depot-02'],  zoneId: 'atelier',  icon: Wrench,    color: 'stone' },
  { id: 'inv-energy-01',layer: 2, column: 4, parents: ['inv-depot-02'],  zoneId: 'pompe',    icon: Fuel,      color: 'red' },

  // ─── NIVEAU 3 — Déblocables après le quai / la cuve ────────────────
  { id: 'inv-depot-05', layer: 3, column: 0, parents: ['inv-depot-04'],  zoneId: 'frigo',          icon: Thermometer,     color: 'cyan' },
  { id: 'inv-depot-07', layer: 3, column: 1, parents: ['inv-depot-04'],  zoneId: 'quai',           icon: HardHat,         color: 'orange' },
  { id: 'inv-depot-08', layer: 3, column: 2, parents: ['inv-depot-04'],  zoneId: 'entrepot',       icon: Box,             color: 'amber' },
  { id: 'inv-depot-11', layer: 3, column: 3, parents: ['inv-depot-04'],  zoneId: 'borne-recharge', icon: BatteryCharging, color: 'lime' },
  { id: 'inv-energy-02',layer: 3, column: 4, parents: ['inv-energy-01'], zoneId: 'pompe',          icon: Droplet,         color: 'red' },

  // ─── NIVEAU 4 — Premium / phase tardive ────────────────────────────
  { id: 'inv-depot-10',   layer: 4, column: 1, parents: ['inv-depot-07'], zoneId: 'zone-adr', icon: AlertTriangle, color: 'orange' },
  { id: 'inv-depot-solar',layer: 4, column: 3, parents: [],               zoneId: 'atelier',  icon: Sun,           color: 'yellow' },
];

/** Index by id for fast lookup. */
export const UPGRADE_TREE_INDEX = Object.fromEntries(
  UPGRADE_TREE_NODES.map(n => [n.id, n])
);

/**
 * Retourne tous les enfants directs d'un nœud (utile pour anim cascade).
 */
export function getChildren(nodeId) {
  return UPGRADE_TREE_NODES.filter(n => n.parents.includes(nodeId));
}

/**
 * Tailwind colour map.  Keep the keys in sync with `node.color` above.
 * Chaque variante : { borderActive, borderAvail, borderLocked, ring, glow }
 */
export const COLOR_TOKENS = {
  amber:   { borderActive: 'border-amber-400',   bg: 'bg-amber-500/10',   text: 'text-amber-300',   glow: 'shadow-amber-500/30',   ring: 'ring-amber-400/40'  },
  emerald: { borderActive: 'border-emerald-400', bg: 'bg-emerald-500/10', text: 'text-emerald-300', glow: 'shadow-emerald-500/30', ring: 'ring-emerald-400/40' },
  cyan:    { borderActive: 'border-cyan-400',    bg: 'bg-cyan-500/10',    text: 'text-cyan-300',    glow: 'shadow-cyan-500/30',    ring: 'ring-cyan-400/40' },
  sky:     { borderActive: 'border-sky-400',     bg: 'bg-sky-500/10',     text: 'text-sky-300',     glow: 'shadow-sky-500/30',     ring: 'ring-sky-400/40' },
  orange:  { borderActive: 'border-orange-400',  bg: 'bg-orange-500/10',  text: 'text-orange-300',  glow: 'shadow-orange-500/30',  ring: 'ring-orange-400/40' },
  red:     { borderActive: 'border-rose-400',    bg: 'bg-rose-500/10',    text: 'text-rose-300',    glow: 'shadow-rose-500/30',    ring: 'ring-rose-400/40' },
  lime:    { borderActive: 'border-lime-400',    bg: 'bg-lime-500/10',    text: 'text-lime-300',    glow: 'shadow-lime-500/30',    ring: 'ring-lime-400/40' },
  stone:   { borderActive: 'border-stone-400',   bg: 'bg-stone-500/10',   text: 'text-stone-300',   glow: 'shadow-stone-500/30',   ring: 'ring-stone-400/40' },
  yellow:  { borderActive: 'border-yellow-400',  bg: 'bg-yellow-500/10',  text: 'text-yellow-300',  glow: 'shadow-yellow-500/30',  ring: 'ring-yellow-400/40' },
};
